import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";

export const DEMO_REQUEST_STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "meeting_booked", label: "Meeting Booked" },
  { value: "demo_completed", label: "Demo Completed" },
  { value: "closed", label: "Closed" },
];

export const PRESENTPLUS_MODULES = [
  "Attendance",
  "Parent App",
  "Communication",
  "Reports",
  "Behaviour",
  "Staff App",
  "Absence Management",
  "Safeguarding",
];

export const CONTRACT_STATUSES = [
  { value: "not_started", label: "Not Started" },
  { value: "proposal_sent", label: "Proposal Sent" },
  { value: "negotiating", label: "Negotiating" },
  { value: "agreement_sent", label: "Agreement Sent" },
  { value: "signed", label: "Signed" },
  { value: "live", label: "Live Customer" },
  { value: "paused", label: "Paused" },
  { value: "ended", label: "Ended" },
];

export const ONBOARDING_STATUSES = [
  { value: "not_started", label: "Not Started" },
  { value: "discovery", label: "Discovery" },
  { value: "data_setup", label: "Data Setup" },
  { value: "staff_training", label: "Staff Training" },
  { value: "parent_launch", label: "Parent Launch" },
  { value: "live", label: "Live" },
  { value: "paused", label: "Paused" },
];

export function normaliseDemoStatus(value) {
  const raw = String(value || "new").trim().toLowerCase();
  const aliases = {
    booked: "meeting_booked",
    meeting: "meeting_booked",
    completed: "demo_completed",
    demo_complete: "demo_completed",
  };

  return aliases[raw] || raw.replace(/\s+/g, "_");
}

export function statusLabel(status) {
  return (
    DEMO_REQUEST_STATUSES.find(
      (item) => item.value === normaliseDemoStatus(status)
    )?.label || "New"
  );
}

export function subscribeToDemoRequests(onData, onError) {
  return onSnapshot(
    collection(db, "demoRequests"),
    (snapshot) => {
      const rows = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      rows.sort(
        (a, b) =>
          getMillis(b.createdAt || b.submittedAt) -
          getMillis(a.createdAt || a.submittedAt)
      );

      onData(rows);
    },
    onError
  );
}

export async function updateDemoRequestStatus(
  requestId,
  previousStatus,
  nextStatus,
  actorName
) {
  const fromStatus = normaliseDemoStatus(previousStatus);
  const status = normaliseDemoStatus(nextStatus);

  if (!requestId || fromStatus === status) return;

  const now = Timestamp.now();
  const ref = doc(db, "demoRequests", requestId);

  const updates = {
    status,
    lastUpdated: now,
    updatedAt: now,
    timeline: arrayUnion({
      type: "status_changed",
      action: "Status changed",
      detail: `${statusLabel(fromStatus)} → ${statusLabel(status)}`,
      previousStatus: fromStatus,
      status,
      actor: actorName || "Super Admin",
      date: now,
      createdAt: now,
    }),
  };

  if (status === "contacted") updates.contactedAt = now;
  if (status === "meeting_booked") updates.meetingBookedAt = now;
  if (status === "demo_completed") updates.completedAt = now;
  if (status === "closed") updates.closedAt = now;

  await updateDoc(ref, updates);
}

export async function appendDemoRequestNote(
  requestId,
  existingNotes,
  newNote,
  actorName
) {
  const cleanNote = String(newNote || "").trim();

  if (!requestId || !cleanNote) {
    throw new Error("Please enter a note before saving.");
  }

  const now = Timestamp.now();
  const actor = actorName || "Super Admin";
  const heading = `${formatNoteDate(now)} — ${actor}`;
  const noteBlock = `${heading}\n${cleanNote}`;
  const previous = String(existingNotes || "").trim();
  const combinedNotes = previous
    ? `${previous}\n\n────────────────────\n\n${noteBlock}`
    : noteBlock;

  await updateDoc(doc(db, "demoRequests", requestId), {
    notes: combinedNotes,
    notesUpdatedAt: now,
    notesUpdatedBy: actor,
    lastUpdated: now,
    updatedAt: now,
    timeline: arrayUnion({
      type: "note_added",
      action: "Internal note added",
      detail: cleanNote,
      actor,
      date: now,
      createdAt: now,
    }),
  });
}

export async function updateDemoRequestSchoolProfile(
  requestId,
  schoolProfile,
  actorName
) {
  if (!requestId) {
    throw new Error("The demo request ID is missing.");
  }

  const actor = actorName || "Super Admin";
  const now = Timestamp.now();
  const cleanProfile = {
    modulesOfInterest: Array.isArray(schoolProfile?.modulesOfInterest)
      ? schoolProfile.modulesOfInterest.filter(Boolean)
      : [],
    meeting: {
      date: String(schoolProfile?.meeting?.date || ""),
      time: String(schoolProfile?.meeting?.time || ""),
      meetingLink: String(schoolProfile?.meeting?.meetingLink || "").trim(),
      location: String(schoolProfile?.meeting?.location || "").trim(),
    },
    contract: {
      status: String(schoolProfile?.contract?.status || "not_started"),
      plan: String(schoolProfile?.contract?.plan || "").trim(),
      annualValue: String(schoolProfile?.contract?.annualValue || "").trim(),
      renewalDate: String(schoolProfile?.contract?.renewalDate || ""),
    },
    onboarding: {
      status: String(schoolProfile?.onboarding?.status || "not_started"),
      progress: Math.max(
        0,
        Math.min(100, Number(schoolProfile?.onboarding?.progress || 0))
      ),
    },
    tasks: Array.isArray(schoolProfile?.tasks)
      ? schoolProfile.tasks
          .map((task) => ({
            text: String(task?.text || task || "").trim(),
            completed: Boolean(task?.completed),
          }))
          .filter((task) => task.text)
      : [],
    updatedAt: now,
    updatedBy: actor,
  };

  await updateDoc(doc(db, "demoRequests", requestId), {
    schoolProfile: cleanProfile,
    lastUpdated: now,
    updatedAt: now,
    timeline: arrayUnion({
      type: "school_profile_updated",
      action: "School profile updated",
      detail: "Modules, meeting, contract, onboarding and tasks were updated.",
      actor,
      date: now,
      createdAt: now,
    }),
  });
}

export async function deleteDemoRequest(requestId) {
  if (!requestId) {
    throw new Error("The demo request ID is missing.");
  }

  await deleteDoc(doc(db, "demoRequests", requestId));
}

export function buildDemoEmail({
  template = "initial",
  contactName = "",
  schoolName = "",
}) {
  const firstName =
    String(contactName || "").trim().split(/\s+/)[0] || "there";
  const school = String(schoolName || "your school").trim();

  const templates = {
    initial: {
      subject: `PresentPlus demonstration for ${school}`,
      body: `Hello ${firstName},

Thank you for requesting a demonstration of PresentPlus.

I would be delighted to show you how PresentPlus can support attendance, parent communication and school administration at ${school}.

Please let me know a convenient day and time for the demonstration.

Kind regards,

Daniel Awoyoolu
CoreLogic Systems Ltd
PresentPlus`,
    },
    follow_up: {
      subject: `Following up: PresentPlus demonstration for ${school}`,
      body: `Hello ${firstName},

I am following up regarding your interest in PresentPlus.

I would be happy to arrange a convenient demonstration and answer any questions you may have about attendance, parent communication, reporting and school administration.

Please let me know a suitable day and time.

Kind regards,

Daniel Awoyoolu
CoreLogic Systems Ltd
PresentPlus`,
    },
    booked: {
      subject: `Your PresentPlus demonstration`,
      body: `Hello ${firstName},

Thank you for arranging a PresentPlus demonstration.

I look forward to showing you the platform and discussing how it can support ${school}.

Please reply to this email if there is a particular area you would like us to focus on during the demonstration.

Kind regards,

Daniel Awoyoolu
CoreLogic Systems Ltd
PresentPlus`,
    },
    after_demo: {
      subject: `Thank you for your time – PresentPlus`,
      body: `Hello ${firstName},

Thank you for taking the time to view PresentPlus.

It was a pleasure learning more about ${school} and discussing how PresentPlus could support your attendance, parent communication and administrative processes.

Please feel free to send over any further questions. I would be happy to discuss the next steps.

Kind regards,

Daniel Awoyoolu
CoreLogic Systems Ltd
PresentPlus`,
    },
    proposal: {
      subject: `PresentPlus next steps for ${school}`,
      body: `Hello ${firstName},

Thank you for your interest in moving forward with PresentPlus.

I am preparing the next-step information for ${school}, including the proposed onboarding approach and the areas discussed during the demonstration.

Please let me know if there is anything else you would like included.

Kind regards,

Daniel Awoyoolu
CoreLogic Systems Ltd
PresentPlus`,
    },
  };

  return templates[template] || templates.initial;
}

export function getMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  if (typeof value._seconds === "number") return value._seconds * 1000;

  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function formatNoteDate(value) {
  const millis = getMillis(value);

  return new Date(millis).toLocaleString("en-GB", {
    timeZone: "Europe/London",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
